import * as React from "react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import {
  clampWorkspaceDrawerWidth,
  getWorkspaceActivePineCardMinWidth,
  getWorkspaceDrawerMaxWidth,
  readWorkspaceDrawerWidth,
  resolveWorkspaceDrawerFullWidthToggle,
  writeWorkspaceDrawerWidth,
  type WorkspaceDrawerWidthKind,
  type WorkspaceDrawerWidthOptions,
} from "./workspaceDrawerWidth";

export type UseWorkspaceDrawerWidthOptions = WorkspaceDrawerWidthOptions & {
  /** When set on a strategy drawer, open/min width tracks the Active Pinescript card. */
  strategyPinePanelCount?: number;
};

/** Prefer the sheet host so max width matches the padded content area. */
const CONTENT_SELECTORS = [
  "[data-workspace-drawer-host]",
  "[data-workspace-canvas-viewport]",
] as const;

function readContentWidth(): number {
  if (typeof window === "undefined") return 0;
  for (const selector of CONTENT_SELECTORS) {
    const el = document.querySelector(selector);
    if (el instanceof HTMLElement) {
      const width = el.getBoundingClientRect().width;
      if (width > 0) return width;
    }
  }
  return window.innerWidth;
}

/** Live master or active pine card width when the dock has rendered a panel. */
function readPineDockPanelWidth(): number | null {
  if (typeof window === "undefined") return null;
  for (const selector of [
    "[data-strategy-active-pine-card]",
    "[data-strategy-master-pine-card]",
  ]) {
    const el = document.querySelector(selector);
    if (!(el instanceof HTMLElement)) continue;
    const width = el.getBoundingClientRect().width;
    if (width > 0) return Math.round(width);
  }
  return null;
}

function shouldUsePineCardSheetMin(kind: WorkspaceDrawerWidthKind): boolean {
  return kind === "strategy" || kind === "detail";
}

function resolveDrawerWidthOptions(
  contentWidth: number,
  kind: WorkspaceDrawerWidthKind,
  options?: UseWorkspaceDrawerWidthOptions
): WorkspaceDrawerWidthOptions {
  const maxWidthCap = options?.maxWidthCap;
  if (!shouldUsePineCardSheetMin(kind) || contentWidth <= 0) {
    if (options?.minWidth != null || options?.preferredWidth != null || maxWidthCap != null) {
      return {
        minWidth: options?.minWidth,
        preferredWidth: options?.preferredWidth,
        maxWidthCap,
      };
    }
    return {};
  }

  const computed = getWorkspaceActivePineCardMinWidth(contentWidth);
  const measured = readPineDockPanelWidth();
  const pineWidth =
    measured != null && Math.abs(measured - computed) <= Math.max(24, computed * 0.12)
      ? measured
      : computed;
  const explicitMin = options?.minWidth;
  const resolved: WorkspaceDrawerWidthOptions = {
    minWidth: explicitMin != null ? Math.max(explicitMin, pineWidth) : pineWidth,
    maxWidthCap,
  };

  if (
    kind === "strategy" &&
    options?.strategyPinePanelCount != null &&
    options.strategyPinePanelCount > 0
  ) {
    resolved.preferredWidth = pineWidth;
  }

  return resolved;
}

export function useWorkspaceDrawerWidth(
  kind: WorkspaceDrawerWidthKind,
  options?: UseWorkspaceDrawerWidthOptions
): {
  width: number;
  maxWidth: number;
  minWidth: number;
  setWidth: (width: number) => void;
  toggleFullWidth: () => void;
  isResizing: boolean;
  beginResize: (event: React.PointerEvent<HTMLElement>) => void;
} {
  const [contentWidth, setContentWidth] = useState(() => readContentWidth());
  const widthOptions = useMemo(
    () => resolveDrawerWidthOptions(contentWidth, kind, options),
    [
      contentWidth,
      kind,
      options?.minWidth,
      options?.maxWidthCap,
      options?.preferredWidth,
      options?.strategyPinePanelCount,
    ]
  );
  const [width, setWidthState] = useState(() => {
    const cw = readContentWidth();
    const opts = resolveDrawerWidthOptions(cw, kind, options);
    // Strategy: land on Active-card width immediately — never the shared 900px detail default.
    if (kind === "strategy") {
      const preferred =
        opts.preferredWidth ??
        getWorkspaceActivePineCardMinWidth(cw, options?.strategyPinePanelCount ?? 2);
      return clampWorkspaceDrawerWidth(preferred, cw, {
        ...opts,
        minWidth: opts.minWidth ?? preferred,
        preferredWidth: preferred,
      });
    }
    return readWorkspaceDrawerWidth(kind, cw, opts);
  });
  const [isResizing, setIsResizing] = useState(false);
  const lastPreferredRef = useRef<number | null>(
    kind === "strategy" ? widthOptions.preferredWidth ?? null : null
  );
  /** Width before a double-click expand to full content; restored on the next toggle. */
  const widthBeforeFullRef = useRef<number | null>(null);
  const minWidth = useMemo(
    () => clampWorkspaceDrawerWidth(0, contentWidth, widthOptions),
    [contentWidth, widthOptions]
  );

  useEffect(() => {
    if (typeof window === "undefined") return;

    const observed: HTMLElement[] = [];
    for (const selector of CONTENT_SELECTORS) {
      const el = document.querySelector(selector);
      if (el instanceof HTMLElement) observed.push(el);
    }
    const pine = document.querySelector(
      "[data-strategy-active-pine-card], [data-strategy-master-pine-card]"
    );
    if (pine instanceof HTMLElement) observed.push(pine);
    const pineDock = document.querySelector("[data-workspace-pine-dock]");
    if (pineDock instanceof HTMLElement) observed.push(pineDock);

    if (observed.length === 0) {
      const onResize = () => setContentWidth(window.innerWidth);
      window.addEventListener("resize", onResize);
      return () => window.removeEventListener("resize", onResize);
    }

    const update = () => setContentWidth(readContentWidth());
    update();
    const observer = new ResizeObserver(update);
    for (const el of observed) observer.observe(el);
    return () => observer.disconnect();
  }, [options?.strategyPinePanelCount]);

  useEffect(() => {
    if (kind === "strategy" && widthOptions.preferredWidth != null) {
      setWidthState(
        clampWorkspaceDrawerWidth(widthOptions.preferredWidth, contentWidth, widthOptions)
      );
      lastPreferredRef.current = widthOptions.preferredWidth;
      return;
    }
    setWidthState(readWorkspaceDrawerWidth(kind, contentWidth, widthOptions));
    lastPreferredRef.current = null;
  }, [kind]);

  useEffect(() => {
    if (isResizing) return;

    setWidthState((current) => {
      if (kind === "strategy" && widthOptions.preferredWidth != null) {
        const preferred = widthOptions.preferredWidth;
        const preferredChanged =
          lastPreferredRef.current == null ||
          Math.abs(lastPreferredRef.current - preferred) > 8;
        lastPreferredRef.current = preferred;

        // Strategy sheets open at Active Pinescript card width (not the shared
        // report/sim detail size). Re-snap when the Active card size changes.
        if (preferredChanged) {
          return clampWorkspaceDrawerWidth(preferred, contentWidth, widthOptions);
        }
      }
      return clampWorkspaceDrawerWidth(current, contentWidth, widthOptions);
    });
  }, [contentWidth, isResizing, kind, widthOptions]);

  const setWidth = useCallback(
    (next: number) => {
      const clamped = clampWorkspaceDrawerWidth(next, contentWidth, widthOptions);
      setWidthState(clamped);
      writeWorkspaceDrawerWidth(kind, clamped, contentWidth, widthOptions);
    },
    [contentWidth, kind, widthOptions]
  );

  const toggleFullWidth = useCallback(() => {
    const maxWidth = getWorkspaceDrawerMaxWidth(contentWidth, widthOptions);
    const { nextWidth, nextWidthBeforeFull } = resolveWorkspaceDrawerFullWidthToggle({
      width,
      maxWidth,
      minWidth,
      widthBeforeFull: widthBeforeFullRef.current,
    });
    widthBeforeFullRef.current = nextWidthBeforeFull;
    setWidth(nextWidth);
  }, [contentWidth, minWidth, setWidth, width, widthOptions]);

  const beginResize = useCallback(
    (event: React.PointerEvent<HTMLElement>) => {
      if (event.button !== 0) return;
      event.preventDefault();
      event.stopPropagation();

      const startX = event.clientX;
      const startWidth = width;
      let lastX = startX;
      let finished = false;
      const pointerId = event.pointerId;
      const target = event.currentTarget;
      const previousCursor = document.body.style.cursor;
      const previousUserSelect = document.body.style.userSelect;

      setIsResizing(true);
      document.body.style.cursor = "col-resize";
      document.body.style.userSelect = "none";
      target.setPointerCapture(pointerId);

      const restoreBody = () => {
        document.body.style.cursor = previousCursor;
        document.body.style.userSelect = previousUserSelect;
      };

      const detach = () => {
        target.removeEventListener("pointermove", onPointerMove);
        target.removeEventListener("pointerup", finish);
        target.removeEventListener("pointercancel", finish);
        window.removeEventListener("pointerup", finish);
        window.removeEventListener("pointercancel", finish);
        window.removeEventListener("blur", onBlur);
        try {
          target.releasePointerCapture(pointerId);
        } catch {
          // already released
        }
      };

      const onPointerMove = (moveEvent: PointerEvent) => {
        lastX = moveEvent.clientX;
        // Right-side drawer: drag handle left → wider.
        const next = startWidth + (startX - lastX);
        setWidthState(
          clampWorkspaceDrawerWidth(next, readContentWidth(), widthOptions)
        );
      };

      const finish = () => {
        if (finished) return;
        finished = true;
        const liveContentWidth = readContentWidth();
        const next = clampWorkspaceDrawerWidth(
          startWidth + (startX - lastX),
          liveContentWidth,
          widthOptions
        );
        setWidthState(next);
        writeWorkspaceDrawerWidth(kind, next, liveContentWidth, widthOptions);
        setIsResizing(false);
        restoreBody();
        detach();
      };

      const onBlur = () => {
        finish();
      };

      target.addEventListener("pointermove", onPointerMove);
      target.addEventListener("pointerup", finish);
      target.addEventListener("pointercancel", finish);
      // Fallback if capture is lost mid-drag (keeps body cursor from sticking).
      window.addEventListener("pointerup", finish);
      window.addEventListener("pointercancel", finish);
      window.addEventListener("blur", onBlur);
    },
    [kind, width, widthOptions]
  );

  useEffect(() => {
    return () => {
      // If the sheet unmounts mid-drag, never leave col-resize on the page.
      document.body.style.cursor = "";
      document.body.style.userSelect = "";
    };
  }, []);

  return {
    width,
    maxWidth: getWorkspaceDrawerMaxWidth(contentWidth, widthOptions),
    minWidth,
    setWidth,
    toggleFullWidth,
    isResizing,
    beginResize,
  };
}
